// src/lib/stay/rooms.ts
//
// Shared primitives for Zamzi Stay multi-room support.
//
// Data model: a room is its own profile document in `profiles` — the same
// shape the booking engine already runs per profile — linked to the host's
// property (whose document id is the host's uid) via `parentPropertyId`.
// Because of this, per-room iCal sync, bookings and availability work through
// the existing engine unchanged.
//
// Exclusion invariants (why rooms never appear publicly and never get billed):
//   - `profileType: "stay-room"` is not in the functions' ONCE_OFF_TYPES or
//     SUBSCRIPTION_TYPES sets, so no renewal/deactivation sweep touches it.
//   - `isPublic: false` and `subscriptionStatus: "included"` keep rooms out of
//     every public search query (which requires isPublic + active status).

export const STAY_ROOM_PROFILE_TYPE = "stay-room";
export const MAX_ROOMS_PER_PROPERTY = 10;

const DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;
const DAY_MS = 24 * 60 * 60 * 1000;

export const HOST_BLOCK_SOURCE = "host_block";

export type StayRoomFields = {
  profileType: typeof STAY_ROOM_PROFILE_TYPE;
  userId: string;
  parentPropertyId: string;
  roomName: string;
  businessName: string;
  sleeps: number | null;
  // Host-picked references into the property's own gallery — the guest-facing
  // Rooms section and room picker read these. Optional: rooms predate photos.
  stayRoomPhotoUrls?: string[];
  // Host labels for the room's incoming OTA feeds ("Airbnb"...), index-aligned
  // to externalCalendarUrls. Presentational only — functions/ reads the urls.
  externalCalendarNames?: string[];
  isPublic: false;
  subscriptionStatus: "included";
  createdAt: unknown;
  updatedAt: unknown;
};

export const normalizeText = (value: unknown): string =>
  typeof value === "string" ? value.trim() : "";

/**
 * Capacity gate for the guest-facing room picker: a party cannot book a
 * room whose capacity is smaller than the group. null sleeps = unknown, so
 * the room stays bookable (the engine keeps its own guard).
 */
export const isRoomTooSmall = (sleeps: number | null | undefined, guests: number): boolean =>
  typeof sleeps === "number" && sleeps > 0 && guests > sleeps;

export const MAX_CALENDAR_CHANNELS = 5;
export const MAX_CHANNEL_LABEL_CHARS = 40;

/**
 * Channel labels for a room's incoming iCal feeds ("Airbnb", "Booking.com"...).
 * Stored as a NEW parallel field `externalCalendarNames` — index-aligned to
 * `externalCalendarUrls` — because the guest-calendar sync in functions/ reads
 * the urls array directly and must not have its shape changed.
 */
export const normalizeCalendarChannelNames = (
  urls: readonly string[],
  rawNames: unknown,
  maxChannels: number = MAX_CALENDAR_CHANNELS,
): string[] => {
  const names = Array.isArray(rawNames)
    ? rawNames.map((value) => normalizeText(value).slice(0, MAX_CHANNEL_LABEL_CHARS))
    : [];
  return urls.slice(0, maxChannels).map((_, index) => names[index] || "");
};

/** Matches the gallery's own 30-photo cap — room photos are picks from it. */
export const MAX_PHOTOS_PER_ROOM = 30;

/**
 * Room photos cross three trust boundaries (owner PATCH, public availability
 * API, page catalog reads), so one normalizer keeps the public surface limited
 * to clean https URLs no matter how the field was written.
 */
export const normalizeRoomPhotoUrls = (value: unknown): string[] => {
  if (!Array.isArray(value)) return [];
  const seen = new Set<string>();
  const urls: string[] = [];
  for (const item of value) {
    const url = normalizeText(item);
    if (!/^https:\/\//i.test(url) || url.length > 2048) continue;
    if (seen.has(url)) continue;
    seen.add(url);
    urls.push(url);
    if (urls.length >= MAX_PHOTOS_PER_ROOM) break;
  }
  return urls;
};

/**
 * Mirrors `isOccupyingBooking` in functions/src/index.ts — the single source
 * of truth for "does this booking block?". Kept deliberately in sync: the
 * authoritative check still runs in the engine when a guest books; this copy
 * only powers host-facing conflict reports (block whole house).
 */
export const isOccupyingBookingStatus = (
  status: unknown,
  expiresAtMillis: number | null,
  nowMillis: number,
): boolean => {
  if (status === "confirmed") return true;
  if (status === "pending" || status === "date_locked") {
    // Live holds occupy until they expire; expired holds do not.
    return expiresAtMillis === null || expiresAtMillis > nowMillis;
  }
  return false;
};

export const intervalsOverlap = (
  startA: number,
  endA: number,
  startB: number,
  endB: number,
): boolean => startA < endB && endA > startB;

/**
 * Expands a room's busy ranges ([start, end) ISO day strings, checkout-day
 * exclusive) into the set of busy NIGHT days as YYYY-MM-DD. Powers the
 * guest-facing per-room calendar: booking "3 Nov → 5 Nov" occupies the nights
 * of the 3rd and 4th, and the 5th (checkout morning) stays open.
 */
export const expandBusyRangesToDays = (
  ranges: ReadonlyArray<{ start: unknown; end: unknown }>,
): Set<string> => {
  const days = new Set<string>();
  for (const range of ranges) {
    const startStr = normalizeText(range.start);
    const endStr = normalizeText(range.end);
    if (!DATE_PATTERN.test(startStr) || !DATE_PATTERN.test(endStr)) continue;
    const startMs = Date.parse(`${startStr}T00:00:00Z`);
    const endMs = Date.parse(`${endStr}T00:00:00Z`);
    if (!Number.isFinite(startMs) || !Number.isFinite(endMs) || endMs <= startMs) continue;
    for (let t = startMs; t < endMs; t += DAY_MS) {
      days.add(new Date(t).toISOString().slice(0, 10));
    }
  }
  return days;
};


/**
 * Parses a whole-house block window from YYYY-MM-DD strings. Check-in style
 * dates: `start` is the first blocked night, `end` is the first night NOT
 * blocked (exclusive), matching how booking start/end are stored.
 */
export const parseWholeHouseWindow = (
  start: unknown,
  end: unknown,
  nowMillis: number,
): { startMs: number; endMs: number; days: string[] } | { error: string } => {
  const startStr = normalizeText(start);
  const endStr = normalizeText(end);
  if (!DATE_PATTERN.test(startStr) || !DATE_PATTERN.test(endStr)) {
    return { error: "start and end must be YYYY-MM-DD dates" };
  }
  const startMs = Date.parse(`${startStr}T00:00:00Z`);
  const endMs = Date.parse(`${endStr}T00:00:00Z`);
  if (!Number.isFinite(startMs) || !Number.isFinite(endMs)) {
    return { error: "Invalid dates" };
  }
  if (endMs <= startMs) {
    return { error: "end must be after start" };
  }
  const nights = Math.round((endMs - startMs) / DAY_MS);
  if (nights > 90) {
    return { error: "Whole-house blocks are limited to 90 nights" };
  }
  if (endMs <= nowMillis) {
    return { error: "Block dates cannot be in the past" };
  }
  const days: string[] = [];
  for (let t = startMs; t < endMs; t += DAY_MS) {
    days.push(new Date(t).toISOString().slice(0, 10));
  }
  return { startMs, endMs, days };
};
